import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { Stack } from '@mantine/core';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { FilterBar } from '../../components/FilterBar';
import { useAuditLogs } from '../../hooks/useReports';

interface AuditEntry {
  id: string;
  userId: string;
  action: string;
  objectType: string;
  objectId: string;
  timestamp: string;
}

interface AuditLogResponse {
  auditLog: {
    data: AuditEntry[];
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
  };
}

export function AuditLogPage() {
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const [search, setSearch] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const { data: queryData, loading } = useAuditLogs({
    page,
    perPage: pageSize,
    action: filterValues.action || undefined,
    userId: search || undefined,
    startDate: filterValues.startDate || undefined,
    endDate: filterValues.endDate || undefined,
  });
  const auditData = queryData as unknown as AuditLogResponse | undefined;
  const data = auditData?.auditLog?.data ?? [];
  const total = auditData?.auditLog?.total ?? 0;

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const formatTimestamp = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAction = (action: string) =>
    action
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const columns: Column<AuditEntry>[] = [
    {
      key: 'timestamp',
      label: 'Timestamp',
      sortable: true,
      width: 180,
      render: (row) => formatTimestamp(row.timestamp),
    },
    {
      key: 'userId',
      label: 'User',
      sortable: true,
    },
    {
      key: 'action',
      label: 'Action',
      sortable: true,
      render: (row) => formatAction(row.action),
    },
    {
      key: 'objectType',
      label: 'Object Type',
      sortable: true,
    },
    {
      key: 'objectId',
      label: 'Object ID',
      width: 160,
    },
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title="Audit Log"
        subtitle="Track all system changes and user actions"
      />

      <FilterBar
        searchValue={search}
        onSearchChange={(val) => {
          setSearch(val);
          setPage(1);
        }}
        searchPlaceholder="Search by user..."
        filters={[
          {
            key: 'action',
            label: 'Action',
            options: [
              { value: 'create', label: 'Create' },
              { value: 'update', label: 'Update' },
              { value: 'delete', label: 'Delete' },
              { value: 'login', label: 'Login' },
              { value: 'logout', label: 'Logout' },
              { value: 'approve', label: 'Approve' },
              { value: 'reject', label: 'Reject' },
            ],
          },
        ]}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
      />

      <DataTable<Record<string, unknown>>
        columns={columns as unknown as Column<Record<string, unknown>>[]}
        data={data as unknown as Record<string, unknown>[]}
        loading={loading}
        pagination={{
          page,
          pageSize,
          total,
          onPageChange: setPage,
        }}
        getRowKey={(row) => row.id as string}
        emptyMessage="No audit log entries found"
      />
    </Stack>
  );
}

export default AuditLogPage;
